import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { UserPlus, X } from 'lucide-react';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { SortableTableHead, accountStatus } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import ModalPortal from '../components/ui/ModalPortal';
import { useTimezone } from '@kioskgaming/ui';
import { formatInTimezone } from '@kioskgaming/ui';
import PortalEmptyState from '../components/ui/PortalEmptyState';
import { getTableSortColumns } from '../config/tableSortConfig';
import useTableSort from '../hooks/useTableSort';
import ActivePlayersOnlyCheckbox from '../components/ActivePlayersOnlyCheckbox';
import { ShopPlayerLinkByPhonePanel } from '../components/ShopPlayerLinkByPhonePanel';
import { fetchDashboard, fetchShopPlayers, type ShopPlayersSortKey, type SortOrder } from '../services/api';
import type { ShopPagination, ShopPlayer } from '../types';

function badge(verified: boolean | undefined) {
  if (verified === true) return { text: 'Verified', cls: 'bg-emerald-100 text-emerald-800' };
  return { text: 'Unverified', cls: 'bg-amber-100 text-amber-900' };
}

function playerStatusBadge(player: ShopPlayer) {
  if (player.status === 'terminated') {
    return { label: accountStatus('terminated').label, cls: 'bg-neutral-200 text-neutral-800' };
  }
  if (player.banned || player.status === 'banned') {
    return { label: accountStatus('banned').label, cls: 'bg-red-100 text-red-800' };
  }
  if (player.status === 'active') {
    return { label: accountStatus(player.status).label, cls: 'bg-emerald-100 text-emerald-800' };
  }
  return { label: accountStatus(player.status).label, cls: 'bg-gray-100 text-gray-700' };
}

export default function PlayersPage() {
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const [players, setPlayers] = useState<ShopPlayer[] | null>(null);
  const [pagination, setPagination] = useState<ShopPagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [activeOnly, setActiveOnly] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  const tableSort = useTableSort({
    columns: getTableSortColumns('shopPlayers'),
    onChange: () => setPage(1),
  });
  const [showCreatePlayerModal, setShowCreatePlayerModal] = useState(false);
  const [createPlayerModalKey, setCreatePlayerModalKey] = useState(0);

  const openCreatePlayerModal = () => {
    setCreatePlayerModalKey((k) => k + 1);
    setShowCreatePlayerModal(true);
  };

  const [shopCreditBalance, setShopCreditBalance] = useState<number | null>(null);
  const [shopCreditBalanceLoading, setShopCreditBalanceLoading] = useState(true);

  const loadShopCreditBalance = useCallback(async () => {
    setShopCreditBalanceLoading(true);
    try {
      const res = await fetchDashboard();
      if (res.success && res.data != null) {
        const raw = res.data.credit_balance;
        const n = typeof raw === 'number' ? raw : Number.parseFloat(String(raw));
        setShopCreditBalance(Number.isFinite(n) ? n : null);
      } else {
        setShopCreditBalance(null);
      }
    } catch {
      setShopCreditBalance(null);
    } finally {
      setShopCreditBalanceLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadShopCreditBalance();
  }, [loadShopCreditBalance]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQuery(query.trim());
      setPage(1);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    try {
      const sort = (tableSort.sortBy ?? 'shop_assigned_at') as ShopPlayersSortKey;
      const order = (tableSort.sortOrder ?? 'DESC').toLowerCase() as SortOrder;
      const res = await fetchShopPlayers({
        search: debouncedQuery || undefined,
        page,
        limit,
        sort,
        order,
        activeOnly,
      });
      if (res.success && Array.isArray(res.data)) {
        setPlayers(res.data);
        setPagination(res.pagination || null);
      } else {
        setPlayers([]);
        setPagination(null);
      }
    } catch {
      toast.error('Could not load Players');
      setPlayers([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, [debouncedQuery, page, limit, tableSort.sortBy, tableSort.sortOrder, activeOnly]);

  useEffect(() => {
    void loadPlayers();
  }, [loadPlayers]);

  const headerSort = {
    activeKey: tableSort.sortBy,
    direction: tableSort.sortOrder,
  };
  const isTableBusy = loading;

  return (
    <AppLayout contentFullWidth>
      <div className="mb-6">
        <div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Players</h1>
            <p className="mt-1 text-sm text-gray-500">
              View your Players and register a new Player.
            </p>
          </div>
          <div className="mt-3 flex flex-wrap justify-start gap-2 sm:justify-end">
            <button
              type="button"
              onClick={openCreatePlayerModal}
              className="inline-flex items-center justify-center gap-2 rounded-lg bg-emerald-600 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-700 sm:text-sm"
            >
              <UserPlus className="h-4 w-4" aria-hidden />
              Create Player
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <section>
          <div className="rounded-xl border border-stone-200 bg-white p-3 shadow-sm sm:p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
              <label className="block min-w-0 flex-1">
                <span className="mb-1 block text-xs font-medium text-stone-600">Search</span>
                <input
                  type="search"
                  placeholder="Name, phone, or email"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  className="h-10 w-full rounded-lg border border-stone-200 px-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                />
              </label>
              <div className="shrink-0">
                <span className="mb-1 block text-xs font-medium text-stone-600">Status</span>
                <ActivePlayersOnlyCheckbox
                  checked={activeOnly}
                  onChange={(next: boolean) => {
                    setActiveOnly(next);
                    setPage(1);
                  }}
                  className="w-full sm:w-auto"
                />
              </div>
              <label className="block w-full sm:w-24">
                <span className="mb-1 block text-xs font-medium text-stone-600">Rows</span>
                <select
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                  className="h-10 w-full rounded-lg border border-stone-200 px-3 text-sm outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
              </label>
            </div>
          </div>

          <div className="space-y-3 md:hidden">
            {!loading && players && players.length === 0 && !debouncedQuery ? (
              <div className="rounded-xl border border-emerald-100 bg-white shadow-sm">
                <PortalEmptyState
                  icon="👥"
                  title="No Players yet"
                  description={
                    <>
                      Players appear when you create them or link them to this Shop.
                    </>
                  }
                  primaryAction={{
                    kind: 'button',
                    onClick: openCreatePlayerModal,
                    label: 'Create Player',
                  }}
                />
              </div>
            ) : !loading && players && players.length === 0 && debouncedQuery ? (
              <div className="rounded-xl border border-emerald-100 bg-white shadow-sm">
                <PortalEmptyState
                  icon="🔍"
                  title="No Players found"
                  description="No Players match your search. Try another phone or email, or clear the search box."
                />
              </div>
            ) : !players || players.length === 0 ? (
              <div className="rounded-xl border border-emerald-100 bg-white p-6 shadow-sm">
                <PageLoadingIndicator variant="section" accentColor="#059669" />
              </div>
            ) : (
              players.map((p) => (
                <article
                  key={p.id}
                  className="rounded-xl border border-emerald-100 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-center justify-between gap-3">
                    <Link
                      to={`/players/${p.id}`}
                      className="min-w-0 truncate font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
                      title={p.username ?? undefined}
                    >
                      {p.username ?? '—'}
                    </Link>
                    <span
                      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${playerStatusBadge(p).cls}`}
                    >
                      {playerStatusBadge(p).label}
                    </span>
                  </div>
                  <p className="mt-2 truncate text-xs text-gray-600" title={p.email ?? undefined}>{p.email ?? '—'}</p>
                  <p className="mt-1 text-xs text-gray-600">{p.phone ?? '—'}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                    <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${badge(p.phone_verified).cls}`}>
                      Phone: {badge(p.phone_verified).text}
                    </span>
                    <span className={`inline-flex rounded-full px-2 py-0.5 font-semibold ${badge(p.email_verified).cls}`}>
                      Email: {p.email ? badge(p.email_verified).text : 'Not set'}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-gray-500">Balance: {(p.balance ?? 0).toLocaleString()}</p>
                  <p className="mt-1 text-xs text-gray-500">Date joined: {formatInTimezone(p.shop_assigned_at ?? '', timezone)}</p>
                </article>
              ))
            )}
          </div>

          <div className="mt-4 hidden overflow-hidden rounded-xl border border-emerald-100 bg-white shadow-sm md:block">
            <div className="overflow-x-auto" aria-busy={isTableBusy || undefined}>
              <table className="min-w-full text-left text-sm">
                <thead className="border-b border-gray-100 bg-gray-50/80 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <SortableTableHead
                      sortable
                      label="Username"
                      sortKey="name"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-4 py-3 normal-case"
                    />
                    <th className="px-4 py-3">Phone</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Status</th>
                    <SortableTableHead
                      sortable
                      label="Balance"
                      sortKey="balance"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      align="right"
                      disabled={isTableBusy}
                      className="px-4 py-3 normal-case"
                    />
                    <SortableTableHead
                      sortable
                      label="Date joined"
                      sortKey="shop_assigned_at"
                      activeKey={headerSort.activeKey}
                      direction={headerSort.direction}
                      onSort={tableSort.toggleSort}
                      disabled={isTableBusy}
                      className="px-4 py-3 normal-case"
                    />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {!loading && players && players.length === 0 && !debouncedQuery ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-2 align-top">
                        <PortalEmptyState
                          icon="👥"
                          title="No Players yet"
                          description={
                            <>
                              Players appear when you create them or link them to this Shop.
                            </>
                          }
                          primaryAction={{
                            kind: 'button',
                            onClick: openCreatePlayerModal,
                            label: 'Create Player',
                          }}
                        />
                      </td>
                    </tr>
                  ) : !loading && players && players.length === 0 && debouncedQuery ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-2 align-top">
                        <PortalEmptyState
                          icon="🔍"
                          title="No Players found"
                          description="No Players match your search. Try another phone or email, or clear the search box."
                        />
                      </td>
                    </tr>
                  ) : !players || players.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8">
                        <PageLoadingIndicator variant="inline" accentColor="#059669" />
                      </td>
                    </tr>
                  ) : (
                    players.map((p) => (
                      <tr key={p.id} className="hover:bg-emerald-50/30">
                        <td className="max-w-[10rem] px-4 py-3 font-medium">
                          <Link
                            to={`/players/${p.id}`}
                            className="block truncate font-medium text-blue-600 underline underline-offset-2 hover:text-blue-800"
                            title={p.username ?? undefined}
                          >
                            {p.username ?? '—'}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="flex flex-col gap-1">
                            <span>{p.phone ?? '—'}</span>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge(p.phone_verified).cls}`}>
                                {badge(p.phone_verified).text}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          <div className="flex flex-col gap-1">
                            <span className="block max-w-[16rem] truncate" title={p.email ?? undefined}>{p.email ?? '—'}</span>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ${badge(p.email_verified).cls}`}>
                                {p.email ? badge(p.email_verified).text : 'Not set'}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${playerStatusBadge(p).cls}`}
                          >
                            {playerStatusBadge(p).label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right text-gray-700 whitespace-nowrap">
                          {(p.balance ?? 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {formatInTimezone(p.shop_assigned_at ?? '', timezone)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {!loading && pagination && (
            <div className="mt-3 flex flex-col gap-2 border border-gray-100 bg-white px-4 py-3 text-xs text-gray-500 sm:flex-row sm:items-center sm:justify-between">
              <p>
                Page {pagination.page} / {pagination.totalPages} - Total {pagination.total} player{pagination.total === 1 ? '' : 's'}
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={pagination.page <= 1}
                  className="rounded border border-gray-200 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Prev
                </button>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(pagination.totalPages || 1, prev + 1))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="rounded border border-gray-200 px-2 py-1 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </section>
      </div>

      {showCreatePlayerModal && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-player-modal-title"
        >
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white p-4 shadow-xl sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div>
                <h2 id="create-player-modal-title" className="text-base font-semibold text-gray-900">
                  Create Player
                </h2>
                <p className="mt-1 text-xs text-gray-500">
                  Register a new Player or link an account that has no Shop yet.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowCreatePlayerModal(false)}
                className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
                aria-label="Close create Player modal"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            </div>

            <ShopPlayerLinkByPhonePanel
              key={createPlayerModalKey}
              showPanelHeader={false}
              onLinked={() => {
                void loadPlayers();
                void loadShopCreditBalance();
                setShowCreatePlayerModal(false);
              }}
              onLinkedPlayer={(linked) => {
                setShowCreatePlayerModal(false);
                navigate(`/players/${linked.id}`);
              }}
              onInShop={(normalizedPhone) => setQuery(normalizedPhone)}
            />
          </div>
        </div>
        </ModalPortal>
      )}
    </AppLayout>
  );
}
